import React, { useEffect, useRef, useState, useMemo } from "react";
// === ADDED BY SAMPATH – 2026-01-29 ===
// (useMemo added for centered address text build)
// === END SAMPATH ===

import Button from "@mui/material/Button";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setdeliveryType } from "store/slices/main.slice";

// === ADDED BY SAMPATH – 2026-01-16 ===
// (No new imports needed; using existing tailwind/theme classes)
// === END SAMPATH ===

interface OrderTypePopupProps {
  setStage: (v: string) => void;
}

const OrderType: React.FC<OrderTypePopupProps> = ({ setStage }) => {
  const [orderType, setOrderType] = useState<"pickup" | "delivery">("pickup");
  const dispatch = useAppDispatch();
  const settings = useAppSelector(({ main }) => main.settings);

  // === ADDED BY SAMPATH – 2026-01-16 ===
  // Pull companyData as well (same as DeliveryTypePopUp design)
  const companyData = useAppSelector(({ main }) => main.companyData);

  // Target this popup root so we can find the *current* dialog paper and neutralize it
  const rootRef = useRef<HTMLDivElement | null>(null);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Build centered address line (address1 + address2) similar to your other popup
  const locationText = useMemo(() => {
    const a1 = (companyData?.address1 ?? "").trim();
    const a2 = (companyData?.address2 ?? "").trim();
    const full = `${a1}${a1 && a2 ? " " : ""}${a2}`.trim();
    return full;
  }, [companyData?.address1, companyData?.address2]);

  const showLocationUnderName =
    !!settings?.DisplayLocationOnCheckout && !!locationText;

  // Availability flags (same meaning used elsewhere)
  const isPickupDisabled = !!settings?.ClosePickupOrder;
  const isDeliveryDisabled = !!settings?.CloseDeliveryOrder;

  // Status label requirement
  const unavailableLabel = "Today not Available";

  // Overall store availability summary (if both closed -> Not Available)
  const anyAvailable = !isPickupDisabled || !isDeliveryDisabled;

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // True when ONLY ONE is available (show a red message but DO NOT auto close)
  const isOnlyOneAvailable =
    (isPickupDisabled && !isDeliveryDisabled) ||
    (!isPickupDisabled && isDeliveryDisabled);

  const oneAvailableMsg = isPickupDisabled
    ? "Pickup: Today not Available"
    : "Delivery: Today not Available";
  // === END SAMPATH ===
  // === END SAMPATH ===

  const handleSubmit = () => {
    if (orderType !== "pickup") {
      dispatch(setdeliveryType(orderType));
    } else {
      dispatch(setdeliveryType(orderType));
    }
    setStage("CART");
  };

  useEffect(() => {
    if (settings.ClosePickupOrder !== 1) {
      setOrderType("pickup");

      // === ADDED BY SAMPATH – 2026-01-29 ===
      // DO NOT auto navigate to CART when only one service is active
      // (Removed setStage("CART") auto close behavior)
      // === END SAMPATH ===
      // if (settings.CloseDeliveryOrder === 1) {
      //   dispatch(setdeliveryType("pickup"));
      //   setStage("CART");
      // }
    } else if (settings.CloseDeliveryOrder !== 1) {
      setOrderType("delivery");

      // === ADDED BY SAMPATH – 2026-01-29 ===
      // DO NOT auto navigate to CART when only one service is active
      // (Removed setStage("CART") auto close behavior)
      // === END SAMPATH ===
      // if (settings.ClosePickupOrder === 1) {
      //   dispatch(setdeliveryType("delivery"));
      //   setStage("CART");
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // === ADDED BY SAMPATH – 2026-01-16 ===
  // Ensure full-screen overlay even when rendered inside MUI Dialog/Paper (fix for not covering whole viewport)
  // Also lock body scroll while this popup is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-16 ===
  // REMOVE the unwanted MUI Dialog "Paper" panel behind the overlay (MuiPaper-root...MuiDialog-paper...)
  // We only neutralize the *parent dialog paper* that contains THIS component, and restore on unmount.
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    // Find the closest dialog paper that is wrapping this content
    const paper = rootEl.closest(".MuiDialog-paper") as HTMLElement | null;
    if (!paper) return;

    // Backup existing inline styles so we can restore safely
    const prev = {
      background: paper.style.background,
      boxShadow: paper.style.boxShadow,
      padding: paper.style.padding,
      margin: paper.style.margin,
      minWidth: paper.style.minWidth,
      minHeight: paper.style.minHeight,
      width: paper.style.width,
      height: paper.style.height,
      maxWidth: paper.style.maxWidth,
      maxHeight: paper.style.maxHeight,
      overflow: paper.style.overflow,
      border: paper.style.border,
    };

    // Neutralize the panel
    paper.style.background = "transparent";
    paper.style.boxShadow = "none";
    paper.style.border = "none";
    paper.style.padding = "0";
    paper.style.margin = "0";
    paper.style.minWidth = "0";
    paper.style.minHeight = "0";
    paper.style.width = "auto";
    paper.style.height = "auto";
    paper.style.maxWidth = "none";
    paper.style.maxHeight = "none";
    paper.style.overflow = "visible";

    return () => {
      paper.style.background = prev.background;
      paper.style.boxShadow = prev.boxShadow;
      paper.style.padding = prev.padding;
      paper.style.margin = prev.margin;
      paper.style.minWidth = prev.minWidth;
      paper.style.minHeight = prev.minHeight;
      paper.style.width = prev.width;
      paper.style.height = prev.height;
      paper.style.maxWidth = prev.maxWidth;
      paper.style.maxHeight = prev.maxHeight;
      paper.style.overflow = prev.overflow;
      paper.style.border = prev.border;
    };
  }, []);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Click on START button should go to cart immediately (keeps your handleSubmit function intact)
  const handleStart = (type: "pickup" | "delivery") => {
    if (type === "pickup" && isPickupDisabled) return;
    if (type === "delivery" && isDeliveryDisabled) return;

    setOrderType(type);
    dispatch(setdeliveryType(type));
    setStage("CART");
  };
  // === END SAMPATH ===

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 w-screen h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-black/30 backdrop-blur-[2px]"
    >
      <div className="w-[96vw] max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Top header */}
        <div className="px-6 pt-8 pb-6 flex flex-col items-center text-center">
          {settings?.showLogo !== 0 ? (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="relative"
            >
              <img
                src={companyData?.logoUrl}
                alt=""
                className="mb-4 max-h-20 max-w-[260px] object-contain"
                loading="eager"
                draggable={false}
              />
            </div>
          ) : null}

          {!!settings?.ShowCompanyName && !!companyData?.companyName && (
            <div className="w-full flex flex-col items-center">
              <h1 className="text-lg sm:text-xl font-extrabold text-primary text-center tracking-wide leading-tight break-words whitespace-normal w-full">
                {companyData?.companyName}
              </h1>

              {showLocationUnderName && (
                <p
                  className="mt-1 text-[11px] sm:text-xs text-center text-primary/80 uppercase tracking-[0.12em] leading-snug break-words whitespace-normal w-full"
                  title={locationText}
                >
                  {locationText}
                </p>
              )}

              {/* If BOTH disabled */}
              {!anyAvailable && (
                <div className="mt-3 text-sm font-semibold text-red-600">
                  Not Available Today
                </div>
              )}

              {/* === ADDED BY SAMPATH – 2026-01-29 === */}
              {/* If ONLY one service is available, show Today not Available message (but DO NOT auto close) */}
              {anyAvailable && isOnlyOneAvailable && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {oneAvailableMsg}
                </div>
              )}
              {/* === END SAMPATH === */}
            </div>
          )}
        </div>

        {/* Heading + description */}
        {(settings.CloseDeliveryOrder !== 1 || settings.ClosePickupOrder !== 1) && (
          <div className="px-6 -mt-2 pb-2 flex flex-col items-center text-center">
            <h2 className="text-base sm:text-lg font-bold text-black/80 tracking-wide">
              Choose Your Order Type
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-black/50 max-w-xl">
              Select whether you'd like to pick up your order or have it delivered.
            </p>
          </div>
        )}

        {/* Two columns area */}
        <div className="px-6 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch">
            {/* Pickup column */}
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <div className="h-14 w-14 flex items-center justify-center text-black/70">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 8h12l-1 12H7L6 8z" />
                  <path d="M9 8V6a3 3 0 0 1 6 0v2" />
                </svg>
              </div>

              <div className="mt-6 text-xs font-extrabold tracking-[0.35em] text-black/80">
                ORDER PICK UP
              </div>

              {isPickupDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {unavailableLabel}
                </div>
              )}

              <Button
                variant="outlined"
                disabled={isPickupDisabled}
                onClick={() => handleStart("pickup")}
                sx={{
                  mt: 3,
                  px: 5,
                  py: 1.2,
                  borderRadius: "0px",
                  borderColor: "rgb(var(--color-primary) / 0.95)",
                  color: "rgba(0,0,0,0.75)",
                  letterSpacing: "0.18em",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "rgb(var(--color-primary) / 0.95)",
                    borderWidth: "2px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  },
                }}
              >
                START YOUR ORDER
              </Button>
            </div>

            {/* Divider */}
            <div className="hidden md:flex justify-center">
              <div className="w-px bg-black/15 my-8" />
            </div>

            {/* Delivery column */}
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <div className="h-14 w-14 flex items-center justify-center text-black/70">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 7h11v10H3z" />
                  <path d="M14 10h4l3 3v4h-7z" />
                  <circle cx="7" cy="19" r="1.5" />
                  <circle cx="18" cy="19" r="1.5" />
                </svg>
              </div>

              <div className="mt-6 text-xs font-extrabold tracking-[0.35em] text-black/80">
                GET DELIVERY
              </div>

              {isDeliveryDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {unavailableLabel}
                </div>
              )}

              <Button
                variant="outlined"
                disabled={isDeliveryDisabled}
                onClick={() => handleStart("delivery")}
                sx={{
                  mt: 3,
                  px: 5,
                  py: 1.2,
                  borderRadius: "0px",
                  borderColor: "rgb(var(--color-primary) / 0.95)",
                  color: "rgba(0,0,0,0.75)",
                  letterSpacing: "0.18em",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "rgb(var(--color-primary) / 0.95)",
                    borderWidth: "2px",
                    backgroundColor: "rgba(0,0,0,0.03)",
                  },
                }}
              >
                START YOUR ORDER
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderType;
